import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Camera, CalendarClock, Wrench, Sparkles, Paintbrush, PiggyBank, Timer, TrendingUp, Check } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Shared";
import Seo from "../components/Seo";
import { CtaBanner } from "./Home";
import { process, benefits, business } from "../mock";

const pIcon = { Camera, CalendarClock, Wrench, Sparkles };
const bIcon = { Paintbrush, PiggyBank, Timer, TrendingUp };

const PageHeader = ({ title, crumb, image }) => (
  <section className="relative h-[38vh] min-h-[300px] flex items-center overflow-hidden bg-[#242424]">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white">{title}</h1>
      <div className="mt-3 flex items-center gap-2 text-sm text-gray-300">
        <Link to="/" className="hover:text-[#84bd00]">Home</Link>
        <span className="text-[#84bd00]">/</span>
        <span className="text-[#84bd00]">{crumb}</span>
      </div>
    </div>
  </section>
);

export { PageHeader };

const PaintlessDentRemoval = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Paintless Dent Removal Sydney",
    serviceType: "Paintless Dent Removal",
    provider: { "@type": "AutoRepair", name: "Dentcraft NSW", "@id": "https://dentcraftnsw.au/#business" },
    areaServed: ["Sydney", "Western Sydney", "Sydney Metro"],
    url: "https://dentcraftnsw.au/paintless-dent-removal",
    description: "Paintless dent removal (PDR) in Sydney and Western Sydney \u2014 removing dents without repainting while keeping your factory finish.",
  };
  return (
    <div>
      <Seo
        title="Paintless Dent Repair Sydney (PDR) | Dentcraft NSW"
        description="Paintless dent removal (PDR) in Sydney and Western Sydney. Dentcraft NSW removes dents, dings and creases without repainting, keeping your car's factory finish. Free quote."
        path="/paintless-dent-removal"
        schema={schema}
      />
      <PageHeader
        title="Paintless Dent Removal Sydney"
        crumb="Paintless Dent Removal"
        image="https://images.unsplash.com/photo-1662135008048-dfeaf14fcbfa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      {/* What is PDR */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img
              src="https://images.unsplash.com/photo-1518306727298-4c17e1bf6942?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Paintless dent removal on a car panel in Sydney"
              className="rounded-2xl shadow-xl w-full h-[420px] object-cover"
            />
          </Reveal>
          <Reveal delay={120}>
            <span className="text-[#84bd00] font-semibold text-sm tracking-widest uppercase">What is PDR?</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#242424] leading-tight">
              The smart way to remove dents
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Paintless Dent Removal (PDR) is a specialised technique that gently massages dents out from behind the panel — with no fillers, sanding or repainting. Your original factory paint stays completely intact.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Ideal for hail damage, door dings and minor creases",
                "Preserves your original factory paint finish",
                "Far cheaper and faster than conventional body work",
                "Perfect for new, classic and prestige vehicles",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-[#84bd00] flex items-center justify-center mt-0.5">
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="text-gray-700">{t}</span>
                </li>
              ))}
            </ul>
            <a href={`tel:${business.phoneRaw}`} className="mt-8 inline-flex items-center gap-2 bg-[#84bd00] hover:bg-[#75a800] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              <Phone size={18} /> {business.phone}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#f7f9f2]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Our Process" title="How it works in 4 simple steps" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => {
              const Icon = pIcon[p.icon];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="relative bg-white rounded-2xl p-7 h-full shadow-sm hover:shadow-lg transition-shadow">
                    <span className="absolute top-5 right-6 font-display font-extrabold text-4xl text-[#84bd00]/15">{p.step}</span>
                    <div className="w-13 h-13 w-14 h-14 rounded-2xl bg-[#84bd00]/10 flex items-center justify-center mb-5">
                      <Icon size={26} className="text-[#84bd00]" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#242424] mb-2">{p.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Why Choose PDR" title="The benefits of going paintless" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => {
              const Icon = bIcon[b.icon];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="text-center p-7 rounded-2xl border border-gray-100 hover:border-[#84bd00]/40 hover:bg-[#f7f9f2] transition-colors h-full">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-[#84bd00] flex items-center justify-center mb-5">
                      <Icon size={28} className="text-white" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#242424] mb-2">{b.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default PaintlessDentRemoval;
